import React, { useState } from "react";
import { BaseTable } from "@app/components/common/BaseTable/BaseTable";
import { BaseButton } from "@app/components/common/BaseButton/BaseButton";
import { BaseSpace } from "@app/components/common/BaseSpace/BaseSpace";
import { PageTitle } from "@app/components/common/PageTitle/PageTitle";
import {
  message,
  Modal,
  Input,
  DatePicker,
  Select,
  Form,
  Row,
  Col,
  Tag,
  Drawer,
  Descriptions,
  Card,
  Statistic,
  List,
  Avatar,
  Divider,
  Popconfirm,
} from "antd";
import {
  SearchOutlined,
  EyeOutlined,
  DeleteOutlined,
  BarChartOutlined,
  UserOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import {
  adminApi,
  DailyReport,
  DailyReportStatsData,
  DailyReportComment,
} from "@app/api/admin";
import dayjs from "dayjs";
import { useUpdateEffect } from "@app/hooks/useUpdateEffect";
import { useAppSelector } from "@app/hooks/reduxHooks";
import { initialPagination, initialPaginationProps } from "./CompanyPage";
import { useEmployeeList } from "./employee/EmployeePage";
import { BaseDatePicker } from "@app/components/common/pickers/BaseDatePicker";
import { useDepartment } from "./DepartmentPage";

const { RangePicker } = DatePicker;

const DailyReportPage: React.FC = () => {
  const [searchForm] = Form.useForm();
  const [tableData, setTableData] = useState<{
    data: DailyReport[];
    pagination: initialPaginationProps;
  }>({
    data: [],
    pagination: initialPagination,
  });
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useState<{
    keyword?: string;
    employee_id?: number;
    department_id?: number;
    start_date?: string;
    end_date?: string;
    status?: string;
  }>({});
  const [pagination, setPagination] = useState(initialPagination);
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentReport, setCurrentReport] = useState<DailyReport | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const [statsForm] = Form.useForm();
  const [statsData, setStatsData] = useState<DailyReportStatsData>();
  const user = useAppSelector((state) => state.user.user);
  const [comments, setComments] = useState<DailyReportComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentContent, setCommentContent] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);

  const { employeeList } = useEmployeeList();

  const fetchComments = async (reportId: number) => {
    try {
      setCommentsLoading(true);
      const { data } = await adminApi.dailyReports.getComments(reportId);
      setComments(data || []);
    } catch (error: any) {
      message.error(error.message || "获取评论失败");
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleCreateComment = async () => {
    if (!commentContent.trim() || !currentReport) return;
    try {
      setSubmittingComment(true);
      await adminApi.dailyReports.createComment(currentReport.id, {
        content: commentContent,
      });
      message.success("评论成功");
      setCommentContent("");
      fetchComments(currentReport.id);
    } catch (error: any) {
      message.error(error.message || "评论失败");
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      await adminApi.dailyReports.deleteComment(commentId);
      message.success("删除成功");
      if (currentReport) {
        fetchComments(currentReport.id);
      }
    } catch (error: any) {
      message.error(error.message || "删除失败");
    }
  };


  const fetchDailyReports = async () => {
    try {
      setLoading(true);
      const { page, page_size } = pagination;
      const { data } = await adminApi.dailyReports.list({
        page,
        page_size,
        ...searchParams,
      });
      setTableData({
        data: data.reports || [],
        pagination: data.pagination,
      });
    } catch (error: any) {
      message.error(error.message || "获取日报列表失败");
    } finally {
      setLoading(false);
    }
  };

  useUpdateEffect(() => {
    fetchDailyReports();
  }, [searchParams, pagination]);

  const handleSearch = (values: any) => {
    const dateRange = values.dateRange;
    setSearchParams({
      keyword: values.keyword?.trim(),
      employee_id: values.employee_id,
      department_id: values.department_id,
      status: values.status,
      start_date: dateRange?.[0]?.format("YYYY-MM-DD"),
      end_date: dateRange?.[1]?.format("YYYY-MM-DD"),
    });
    setPagination({ ...initialPagination, page: 1 });
  };

  const handleReset = () => {
    searchForm.resetFields();
    setSearchParams({});
    setPagination({ ...initialPagination, page: 1 });
  };

  const handleViewDetail = async (record: DailyReport) => {
    try {
      setDetailLoading(true);
      const { data } = await adminApi.dailyReports.detail(record.id);
      setCurrentReport(data);
      setDetailVisible(true);
      fetchComments(record.id);
    } catch (error: any) {
      message.error(error.message || "获取日报详情失败");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDeleteRow = async (id: number) => {
    try {
      await adminApi.dailyReports.delete(id);
      fetchDailyReports();
      message.success("删除成功");
    } catch (error: any) {
      message.error(error.message || "删除失败");
    }
  };

  const handleTableChange = (page: number, pageSize: number) => {
    setPagination({
      page,
      page_size: pageSize,
    });
  };

  const handleShowStats = () => {
    statsForm.setFieldsValue({ month: dayjs() });
    handleStatsFilter({ month: dayjs() });
    setStatsVisible(true);
  };

  const handleStatsFilter = async (values: any) => {
    try {
      const { data } = await adminApi.dailyReports.getStats({
        month: values.month?.format("YYYY-MM"),
        department_id: values.department_id,
      });
      setStatsData(data);
    } catch (error: any) {
      message.error(error.message || "获取统计信息失败");
    }
  };

  const [columns] = useState([
    {
      title: "员工姓名",
      key: "employee_name",
      width: 120,
      render: (_: any, record: DailyReport) => record.employee?.name || "-",
    },
    {
      title: "部门",
      key: "department_name",
      width: 120,
      render: (_: any, record: DailyReport) => record.department?.name || "-",
    },
    {
      title: "日期",
      key: "report_date",
      width: 120,
      render: (_: any, record: DailyReport) =>
        record.report_date ? dayjs(record.report_date).format("YYYY-MM-DD") : "-",
    },
    {
      title: "摘要",
      key: "today_work",
      width: 240,
      ellipsis: true,
      render: (_: any, record: DailyReport) => {
        const text = record.today_work || "";
        const trimmed = text.length > 40 ? `${text.slice(0, 40)}...` : text;
        return trimmed || "-";
      },
    },
    // {
    //   title: "工时",
    //   key: "work_hours",
    //   width: 100,
    //   render: (_: any, record: DailyReport) =>
    //     record.work_hours ? `${Number(record.work_hours).toFixed(1)}小时` : "-",
    // },
    {
      title: "状态",
      key: "status",
      width: 100,
      render: (_: any, record: DailyReport) => {
        const status = record.status;
        const color =
          status === "submitted"
            ? "blue"
            : status === "approved"
            ? "green"
            : status === "rejected"
            ? "red"
            : "default";
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "操作",
      key: "action",
      width: 100,
      fixed: 'right',
      render: (_: any, record: DailyReport) => (
        <BaseSpace>
          <BaseButton
            type="primary"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleViewDetail(record)}
            loading={detailLoading}
          >
            详情
          </BaseButton>
          {/* <BaseButton
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleDeleteRow(record.id)}
          >
            删除
          </BaseButton> */}
        </BaseSpace>
      ),
    },
  ]);

  const { departmentList } = useDepartment();

  const renderCommentNode = (item: DailyReportComment) => {
    const isOwner = user?.id === item.commenter_id;
    const isAdmin = user?.is_admin;

    return (
      <List.Item
        actions={
          [
             <Popconfirm
                  key="delete"
                  title="确定删除这条评论吗？"
                  onConfirm={() => handleDeleteComment(item.id)}
                  okText="确定"
                  cancelText="取消"
                >
                  <a style={{ color: "red" }}>删除</a>
                </Popconfirm>,
          ]
        }
      >
        <div style={{ width: "100%" }}>
          <List.Item.Meta
            avatar={<Avatar icon={<UserOutlined />} />}
            title={
              <BaseSpace>
                <span>{item.commenter?.name || "未知用户"}</span>
                <span style={{ color: "#999", fontSize: "12px" }}>
                  {dayjs(item.created_at).format("YYYY-MM-DD HH:mm")}
                </span>
              </BaseSpace>
            }
            description={item.content}
          />
        </div>
      </List.Item>
    );
  };

  return (
    <>
      <PageTitle>日报管理</PageTitle>

      <Form
        form={searchForm}
        layout="inline"
        onFinish={handleSearch}
        style={{ marginBottom: 16 }}
      >
        <Row gutter={16} style={{ width: "100%" }}>
          {/* <Col span={6}>
            <Form.Item name="keyword">
              <Input placeholder="标题/内容" allowClear />
            </Form.Item>
          </Col> */}
          <Col span={6}>
            <Form.Item name="employee_id" >
              <Select
                placeholder="选择员工"
                allowClear
                options={employeeList.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          {/* 部分筛选 */}
          <Col span={6}>
            <Form.Item name="department_id" >
              <Select
                placeholder="选择部门"
                allowClear
                options={departmentList.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item>
              <BaseSpace>
                <BaseButton
                  type="primary"
                  htmlType="submit"
                  icon={<SearchOutlined />}
                  loading={loading}
                >
                  搜索
                </BaseButton>
                <BaseButton onClick={handleReset}>重置</BaseButton>
                <BaseButton icon={<BarChartOutlined />} onClick={handleShowStats}>
                  统计
                </BaseButton>
              </BaseSpace>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <BaseTable
        bordered
        columns={columns as any}
        dataSource={tableData.data}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1600 }}
        pagination={{
          current: tableData.pagination.page,
          pageSize: tableData.pagination.page_size,
          total: tableData.pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          onChange: handleTableChange,
          onShowSizeChange: handleTableChange,
        }}
      />

      <Drawer
        title="日报详情"
        open={detailVisible}
        onClose={() => {
          setDetailVisible(false);
          setCurrentReport(null);
        }}
        width={800}
        destroyOnClose
      >
        {currentReport && (
          <>
            <Descriptions column={1} bordered>
            <Descriptions.Item label="员工姓名">
              {currentReport.employee?.name}
            </Descriptions.Item>
            <Descriptions.Item label="部门">
              {currentReport.department?.name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="日期">
              {currentReport.report_date ? dayjs(currentReport.report_date).format("YYYY-MM-DD") : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="工时">
              {currentReport.work_hours
                ? `${Number(currentReport.work_hours).toFixed(1)}小时`
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag>{currentReport.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="今日工作" span={2}>
              {currentReport.today_work || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="明日计划" span={2}>
              {currentReport.tomorrow_plan || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="问题" span={2}>
              {currentReport.problems || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="需协助" span={2}>
              {currentReport.need_help || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {currentReport.created_at ? dayjs(currentReport.created_at).format("YYYY-MM-DD HH:mm:ss") : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="更新时间">
              {currentReport.updated_at
                ? dayjs(currentReport.updated_at).format("YYYY-MM-DD HH:mm:ss")
                : "-"}
            </Descriptions.Item>
          </Descriptions>
          <>
            <Divider orientation="left">评论</Divider>
            <div style={{ marginTop: 24 }}>
              <List
                className="comment-list"
                loading={commentsLoading}
                itemLayout="horizontal"
                dataSource={comments}
                renderItem={renderCommentNode}
                rowKey="id"
              />
              <div style={{ marginTop: 16 }}>
                <Input.TextArea
                  rows={4}
                  value={commentContent}
                  onChange={(e) => setCommentContent(e.target.value)}
                  placeholder="请输入评论内容..."
                  style={{ marginBottom: 16 }}
                />
                <BaseButton
                  type="primary"
                  onClick={handleCreateComment}
                  loading={submittingComment}
                  disabled={!commentContent.trim()}
                >
                  发表评论
                </BaseButton>
              </div>
            </div>
          </>
          </>
        )}
      </Drawer>

      <Modal
        title="日报统计"
        visible={statsVisible}
        footer={null}
        onCancel={() => setStatsVisible(false)}
        width={800}
      >
        <Form
          form={statsForm}
          layout="inline"
          initialValues={{ month: dayjs() }}
          onFinish={handleStatsFilter}
          style={{ marginBottom: 24 }}
        >
          <Form.Item label="月份" name="month">
            <BaseDatePicker
              picker="month"
              format="YYYY-MM"
              style={{ width: 150 }}
            />
          </Form.Item>
          <Form.Item label="部门" name="department_id">
            <Select
              options={departmentList.map((item) => ({
                label: item.name,
                value: item.id,
              }))}
              style={{ width: 150 }}
              allowClear
            />
          </Form.Item>
          <Form.Item>
            <BaseButton type="primary" htmlType="submit">
              查询
            </BaseButton>
          </Form.Item>
        </Form>

        {statsData && (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Card>
                  <Statistic title="总日报数" value={statsData.total_reports} />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic title="已提交数" value={statsData.submitted_reports} />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic title="草稿数" value={statsData.draft_reports} />
                </Card>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="提交率"
                    value={statsData.submit_rate}
                    suffix="%"
                    precision={2}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="平均工时"
                    value={statsData.avg_work_hours || 0}
                    precision={1}
                    suffix="小时"
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="总工时"
                    value={statsData.total_work_hours || 0}
                    precision={1}
                    suffix="小时"
                  />
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Modal>
    </>
  );
};

export default DailyReportPage;
